// src/components/orderentry/panels/SalesRepsPanel.jsx
// ============================================================================
// AREA 6 — SALES REPS (3-column list box)
//
// Responsibilities:
//  - Display sales reps returned from backend /order-entry/sales-reps
//  - Render as a 3-column list box: Rep (0) | Mngr (1) | TSR (2)
//  - Stay quiet if customer context is not selected
//
// Data contract (backend):
//  salesReps = { div: string[], mgr: string[], tsr: string[] }
//    - div => Manager = 0  (UI column label: Rep)
//    - mgr => Manager = 1  (UI column label: Mngr)
//    - tsr => Manager = 2  (UI column label: TSR)
// ============================================================================

import React from 'react';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import axios from '../../../axiosClient.js';
import EmailComposeDialog from '../components/EmailComposeDialog.jsx';
import GridBadge from '../components/GridBadge.jsx';

// ============================================================================
// BLOCK SRP.1 — Helpers
// ============================================================================

const toArray = (val) => (Array.isArray(val) ? val : []);

// SRP.1.1 — Normalize to { div, no, name }
function toRepArr(x) {
  if (!Array.isArray(x)) {
    return [];
  }

  return x
    .map((v) => {
      // backend object form
      if (v && typeof v === 'object') {
        const div = String(v.div ?? '').trim();
        const no = String(v.no ?? '').trim();
        const name = String(v.name ?? '').trim();
        if (!no && !name) {
          return null;
        }
        const email = String(v.email ?? '').trim();
        return { div, no, name: name || no, email };
      }

      // legacy string form
      const s = String(v ?? '').trim();
      if (!s) {
        return null;
      }

      // patterns:
      // "01-1234 John Smith"
      // "01 1234 John Smith"
      // "1234 John Smith"
      let div = '';
      let no = '';
      let name = s;

      const m1 = s.match(/^(\d{2})\s*[-\s]\s*(\d{1,6})\s+(.*)$/);
      const m2 = s.match(/^(\d{1,6})\s+(.*)$/);

      if (m1) {
        const [, div1, no1, name1] = m1;
        div = div1;
        no = no1;
        name = String(name1 || '').trim();
      } else if (m2) {
        const [, no1, name1] = m2;
        no = no1;
        name = String(name1 || '').trim();
      }

      return { div, no, name };
    })
    .filter(Boolean);
}

// SRP.1.2 — Zip 3 columns to the longest length
function buildRows({ rep, mngr, tsr }) {
  const a = toRepArr(rep);
  const b = toRepArr(mngr);
  const c = toRepArr(tsr);

  const n = Math.max(a.length, b.length, c.length);

  const rows = [];
  for (let i = 0; i < n; i += 1) {
    rows.push({
      rep: a[i] || null,
      mngr: b[i] || null,
      tsr: c[i] || null,
    });
  }
  return rows;
}

// SRP.1.3 — Compact two-line cell: small code (right), name (normal)
// + clickable rep -> fetch email
// + clickable email -> popup compose
function RepCell({ value, meta, onClickRep }) {
  const div0 = String(value?.div ?? '').trim();
  const no0 = String(value?.no ?? '').trim();

  const div1 = String(meta?.div ?? '').trim();
  const no1 = String(meta?.no ?? '').trim();

  const div = div1 || div0;
  const no = no1 || no0;

  const name = String(value?.name ?? '').trim();
  const codeLabel = div && no ? `${div}-${no}` : no;

  if (!no && !name) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  const displayName = name || no;

  return (
    <Box sx={{ minWidth: 0, lineHeight: 1.1 }}>
      {/* code */}
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          textAlign: 'center',
          fontSize: '0.65rem', // ~½ size
          lineHeight: 1.0,
          fontWeight: 500,
          opacity: 0.75,
          whiteSpace: 'nowrap',
        }}
      >
        {codeLabel}
      </Typography>

      {/* name (clickable) */}
      <Typography
        variant="body2"
        sx={{
          lineHeight: 1.1,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          cursor: onClickRep ? 'pointer' : 'default',
          textDecoration: onClickRep ? 'underline' : 'none',
        }}
        title={displayName}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (onClickRep) {
            onClickRep(value);
          }
        }}
      >
        {displayName}
      </Typography>
    </Box>
  );
}

// SRP.1.4 — Build flat part list table for email body
function buildPartListTable(pickingSheets = []) {
  const sheets = toArray(pickingSheets);
  const rows = [];

  sheets.forEach((sheet) => {
    (sheet?.lines || []).forEach((l) => {
      rows.push({
        partNo: String(l.partNo ?? '').trim(),
        qty: l.qty,
        price: l.price,
      });
    });
  });

  if (rows.length === 0) {
    return 'No line items found.';
  }

  const header = `${'PART NO'.padEnd(15) + 'QTY'.padEnd(10)}PRICE`;

  const body = rows.map(
    (r) => r.partNo.padEnd(15) + String(r.qty ?? '').padEnd(10) + String(r.price ?? '')
  );

  return [header, ...body].join('\n');
}

export default function SalesRepsPanel({
  rowHeight,
  salesReps,

  // HOUSE ACCOUNT (optional)
  // Parent passes current context + matched row (if available)
  houseAccountReps,

  // NEW
  shipToName,
  poNumber,
  pickingSheets,
  isCreditHold,
  hasPriceMismatch,
  poPrice,
  contractPrice,
}) {
  // ============================================================================
  // BLOCK SRP.2.EMAIL.0 — Rep email cache + compose dialog (UI only)
  // ----------------------------------------------------------------------------
  // Behavior:
  // - Click rep name: fetch rep email (cached) so it displays under the name
  // - Click email: open compose dialog with To/Subject/Message prefilled
  // - Submit: POST to backend /api/order-entry/send-email
  // ============================================================================

  // Dialog state
  const [emailDialogOpen, setEmailDialogOpen] = React.useState(false);
  const [emailDraft, setEmailDraft] = React.useState({
    to: '',
    subject: '',
    message: '',
    attachment: null,
  });
  const [, setEmailContext] = React.useState(null);

  const buildDraft = React.useCallback(
    ({ to, repObj, sheets }) => {
      const repName = String(repObj?.name ?? '').trim() || 'Team';

      const lines = [];
      lines.push(`Hello ${repName}`);
      lines.push('');
      lines.push(
        `We received an order from ${shipToName || '[Ship-To Name]'} ` +
          `with PO: ${poNumber || '[PO Number]'}.`
      );
      lines.push('');

      // ALWAYS include part list when asking for review
      const partTable = buildPartListTable(sheets);
      lines.push(partTable);
      lines.push('');

      if (isCreditHold) {
        lines.push(
          'The customer is currently on CREDIT HOLD and the order will not ship until this is resolved.'
        );
      } else if (hasPriceMismatch) {
        lines.push('The price on the PO does NOT match the contract pricing.');
        lines.push('');
        lines.push(`PO$: ${poPrice || '[PO Price]'}`);
        lines.push(`Contract$: ${contractPrice || '[Contract Price]'}`);
      }

      lines.push('');
      lines.push('Please review and advise.');

      return {
        to: String(to || '').trim(),
        subject: `Order Entry – ${shipToName || 'Customer'} – Action Required`,
        message: lines.join('\n'),
        attachment: null,
      };
    },
    [shipToName, poNumber, isCreditHold, hasPriceMismatch, poPrice, contractPrice]
  );
  //= ===========================================================================
  //  BLOCK SRP.2.1 — pass picking sheets into draft builder
  //= ===========================================================================

  const openCompose = React.useCallback(
    ({ to, repObj }) => {
      setEmailContext({ repObj });
      setEmailDraft(buildDraft({ to, repObj, sheets: toArray(pickingSheets) }));
      setEmailDialogOpen(true);
    },
    [buildDraft, pickingSheets]
  );

  const closeCompose = React.useCallback(() => {
    setEmailDialogOpen(false);
  }, []);

  const submitCompose = React.useCallback(async (draft) => {
    try {
      await axios.post('/api/order-entry/send-email', {
        to: draft.to,
        subject: draft.subject,
        message: draft.message,
        attachment: draft.attachment || null,
      });

      setEmailDialogOpen(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  }, []);

  // Click rep name → open compose dialog immediately (UI-only)
  // Later: we will fill `to` via backend lookup.

  const handleRepClick = React.useCallback(
    (repObj, row) => {
      const addresses = [row?.rep?.email, row?.mngr?.email, row?.tsr?.email]
        .map((email) => String(email ?? '').trim())
        .filter(Boolean)
        .filter(
          (email, index, all) =>
            all.findIndex((address) => address.toLowerCase() === email.toLowerCase()) === index
        );

      openCompose({ to: addresses.join('; '), repObj });
    },
    [openCompose]
  );

  // ============================================================================
  // BLOCK SRP.HA.0 — House Account Rep override (Rep1/Rep2/TSR)
  // ----------------------------------------------------------------------------
  // Rules (per requirement):
  //  - House mode active ONLY when rep1 exists
  //  - Rep1 = houseAccountReps.rep1 (always present when active)
  //  - Rep2 = houseAccountReps.rep2 OR empty (do NOT allow search to fill)
  //  - TSR = always empty in house mode
  //  - Rep display name should use rep1Name/rep2Name when provided
  // ============================================================================
  const haRep1 = String(houseAccountReps?.rep1 ?? '').trim();
  const haRep2 = String(houseAccountReps?.rep2 ?? '').trim();
  const haRep1Name = String(houseAccountReps?.rep1Name ?? '').trim();
  const haRep2Name = String(houseAccountReps?.rep2Name ?? '').trim();

  const haUse = Boolean(haRep1);

  const haRepObjs = haRep1 ? [{ div: '', no: haRep1, name: haRep1Name || haRep1 }] : [];
  const haMngrObjs = haRep2 ? [{ div: '', no: haRep2, name: haRep2Name || haRep2 }] : [];
  const haTsrObjs = [];

  // SRP.2.2 — Map state buckets -> UI columns
  // If house mode is active, override ALL THREE columns so search data cannot leak in.
  const rep = haUse ? haRepObjs : toArray(salesReps?.rep);
  const mngr = haUse ? haMngrObjs : toArray(salesReps?.mngr);
  const tsr = haUse ? haTsrObjs : toArray(salesReps?.tsr);

  const rows = buildRows({ rep, mngr, tsr });
  const isEmpty = rows.length === 0;

  return (
    <Box sx={{ position: 'relative', minWidth: 0 }}>
      <GridBadge n="6" />

      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        {/* Sales Reps */}
      </Typography>

      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper',
          px: 1,
          py: 0.5,
          minWidth: 0,
          overflowX: 'auto',
          minHeight: rowHeight * 1.2,
          maxHeight: rowHeight * 6,
          overflowY: 'auto',
        }}
      >
        {isEmpty && (
          <Typography variant="body2" color="text.secondary">
            No sales reps found for this state/zip.
          </Typography>
        )}

        {!isEmpty && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: 1,
              alignItems: 'center',
              minWidth: 420,
            }}
          >
            {/* SRP.2.3 — Header row */}
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              Rep
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              Mngr
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              TSR
            </Typography>

            {/* SRP.2.4 — Data rows */}
            {rows.map((r, idx) => {
              const rowKey = [
                r?.rep?.no || r?.rep?.name || '',
                r?.mngr?.no || r?.mngr?.name || '',
                r?.tsr?.no || r?.tsr?.name || '',
                idx,
              ].join('|');

              return (
                <React.Fragment key={rowKey || 'empty-row'}>
                  <RepCell value={r.rep} onClickRep={(repObj) => handleRepClick(repObj, r)} />
                  <RepCell value={r.mngr} onClickRep={(repObj) => handleRepClick(repObj, r)} />
                  <RepCell value={r.tsr} onClickRep={(repObj) => handleRepClick(repObj, r)} />
                </React.Fragment>
              );
            })}
          </Box>
        )}
      </Box>

      {/* =========================================================================
    BLOCK SRP.EMAIL.DIALOG.0 — Compose dialog (UI only)
   ========================================================================= */}
      <EmailComposeDialog
        open={emailDialogOpen}
        title="Email Sales Rep"
        draft={emailDraft}
        onChangeDraft={setEmailDraft}
        onCancel={closeCompose}
        onSubmit={submitCompose}
      />
    </Box>
  );
}
